import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { useMonthlySummary } from "./use-monthly-summary";
import { useRevenueByCountry } from "./use-revenue-by-country";
import { formatMoney, moneyFromCentsString } from "./money";
import { FINANCIAL_REPORT_CURRENCY } from "./constants";
import type { MonthlySummary, RevenueByCountryRow } from "./types";

const LABEL = "font-sans text-sm font-medium text-foreground";

/**
 * The scalar totals, in display order. Keys map to `MonthlySummary` fields
 * (the `satisfies` makes a typo/renamed field fail `tsc`). The two breakdown
 * maps (`byEntryType`/`byAllocationType`) are deliberately NOT rendered here —
 * an open-ended key/value map is a future "see breakdown" drill-down, not scalars.
 */
const SUMMARY_METRICS = [
  "totalRevenue",
  "totalFees",
  "totalTaxes",
  "totalVenueAccruals",
  "totalVenueTransfers",
  "totalRetention",
  "totalChargebacks",
  "totalRefunds",
  "totalLiquidations",
  "netCashPosition",
] as const satisfies ReadonlyArray<keyof MonthlySummary>;

function currentMonth(): string {
  // UTC month on purpose — matches the BE's month-bucketing so viewers in any
  // timezone default to the same accounting month. Only pre-fills the editable
  // <input type="month">, so a boundary-day viewer can correct it if needed.
  return new Date().toISOString().slice(0, 7);
}

export function FinancialReportsPage() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(currentMonth);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const summary = useMonthlySummary(month);
  // Both ends are ISO `yyyy-mm-dd`, so a lexical compare is a valid date compare.
  // An inverted range would make the API return empty / a generic 400 — catch it
  // client-side and skip the request (pass blanks to disable the query).
  const rangeInvalid = from !== "" && to !== "" && from > to;
  const revenue = useRevenueByCountry(rangeInvalid ? "" : from, rangeInvalid ? "" : to);

  return (
    <div data-testid="financial-reports-screen" className="space-y-8">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.reports.eyebrow")}</p>
        <h1 className="text-3xl">{t("finance.reports.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.reports.subtitle")}
        </p>
      </header>

      {/* Monthly summary */}
      <section data-testid="monthly-summary-section" className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="brand-eyebrow">{t("finance.reports.summary.title")}</h2>
          <div className="space-y-1.5">
            <label htmlFor="summary-month" className={LABEL}>
              {t("finance.reports.summary.month")}
            </label>
            <Input
              id="summary-month"
              data-testid="summary-month-input"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
            />
          </div>
        </div>

        <p className="font-sans text-xs text-muted-foreground">
          {t("finance.reports.summary.usdNote")}
        </p>

        {summary.isLoading && (
          <p
            data-testid="monthly-summary-loading"
            className="font-sans text-sm text-muted-foreground"
          >
            {t("finance.reports.summary.loading")}
          </p>
        )}
        {summary.isError && (
          <p
            data-testid="monthly-summary-error"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-sans text-sm text-destructive"
          >
            {t("finance.reports.summary.error")}
          </p>
        )}
        {summary.data && <SummaryTiles summary={summary.data} />}
      </section>

      {/* Revenue by country */}
      <section data-testid="revenue-by-country-section" className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <h2 className="brand-eyebrow">{t("finance.reports.country.title")}</h2>
          <div className="space-y-1.5">
            <label htmlFor="revenue-from" className={LABEL}>
              {t("finance.reports.country.from")}
            </label>
            <Input
              id="revenue-from"
              data-testid="revenue-from-input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="revenue-to" className={LABEL}>
              {t("finance.reports.country.to")}
            </label>
            <Input
              id="revenue-to"
              data-testid="revenue-to-input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        {from === "" || to === "" ? (
          <p data-testid="revenue-prompt" className="font-sans text-sm text-muted-foreground">
            {t("finance.reports.country.prompt")}
          </p>
        ) : rangeInvalid ? (
          <p
            data-testid="revenue-range-error"
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-sans text-sm text-destructive"
          >
            {t("finance.reports.country.rangeError")}
          </p>
        ) : revenue.isLoading ? (
          <p data-testid="revenue-loading" className="font-sans text-sm text-muted-foreground">
            {t("finance.reports.country.loading")}
          </p>
        ) : revenue.isError ? (
          <p
            data-testid="revenue-error"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-sans text-sm text-destructive"
          >
            {t("finance.reports.country.error")}
          </p>
        ) : (revenue.data?.rows.length ?? 0) === 0 ? (
          <p data-testid="revenue-empty" className="font-sans text-sm text-muted-foreground">
            {t("finance.reports.country.empty")}
          </p>
        ) : (
          <ul data-testid="revenue-list" className="space-y-2">
            {revenue.data?.rows.map((row, i) => (
              <RevenueRow
                // country can be null and the API may return several null-country
                // rows sharing currency+month, so the natural key isn't unique —
                // append the index. Rows are a static snapshot (no reorder), so
                // an index tail is stable here.
                key={`${row.country ?? "null"}-${row.currency}-${row.month}-${i}`}
                row={row}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SummaryTiles({ summary }: { readonly summary: MonthlySummary }) {
  const { t } = useTranslation();
  return (
    <div data-testid="monthly-summary-tiles" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {SUMMARY_METRICS.map((key) => (
        <div
          key={key}
          data-testid={`summary-metric-${key}`}
          className="space-y-1 rounded-lg border border-border bg-card p-4"
        >
          <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t(`finance.reports.summary.${key}`)}
          </p>
          <p className="font-display text-xl">
            {formatMoney(moneyFromCentsString(summary[key], FINANCIAL_REPORT_CURRENCY))}
          </p>
        </div>
      ))}
    </div>
  );
}

function RevenueRow({ row }: { readonly row: RevenueByCountryRow }) {
  const { t } = useTranslation();
  return (
    <li
      data-testid={`revenue-row-${row.country ?? "unknown"}-${row.currency}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="space-y-0.5">
        <p className="font-sans text-sm font-medium text-foreground">
          {row.country ?? t("finance.reports.country.unknownCountry")}{" "}
          <span className="uppercase text-muted-foreground">{row.currency}</span>
        </p>
        <p className="font-sans text-xs text-muted-foreground">
          {t("finance.reports.country.invoices", { count: row.invoiceCount })}
          {" · "}
          {row.month}
        </p>
      </div>
      <div className="text-right">
        <p className="font-mono text-sm font-medium text-foreground">
          {formatMoney(moneyFromCentsString(row.grossRevenueCents, row.currency))}
        </p>
        <p className="font-sans text-xs text-muted-foreground">
          {t("finance.reports.country.tax", {
            amount: formatMoney(moneyFromCentsString(row.taxCollectedCents, row.currency)),
          })}
        </p>
      </div>
    </li>
  );
}
